import React from "react";
import { HeaderAction, HeaderState, StyleTab } from "../../types";
import { cssObjectToText, parseCssText } from "../../utils/styleUtils";

interface StylesPanelProps {
	selectedComponentId: string | null;
	state: HeaderState;
	dispatch: React.Dispatch<HeaderAction>;
	activeStyleTab: StyleTab;
	setActiveStyleTab: (tab: StyleTab) => void;
}

const StylesPanel: React.FC<StylesPanelProps> = ({
	selectedComponentId,
	state,
	dispatch,
	activeStyleTab,
	setActiveStyleTab,
}) => {
	const selectedComponent = selectedComponentId
		? state.components.find((comp) => comp.id === selectedComponentId)
		: null;

	const handleHeaderStyleChange = (styles: Record<string, string>) => {
		dispatch({
			type: "UPDATE_HEADER_STYLES",
			payload: styles,
		});
	};

	const handleComponentStyleChange = (styles: Record<string, string>) => {
		if (!selectedComponent) return;

		dispatch({
			type: "UPDATE_COMPONENT",
			payload: {
				id: selectedComponent.id,
				updates: { styles },
			},
		});
	};

	if (!selectedComponent) {
		return (
			<div className="p-4">
				<h3 className="text-lg font-medium mb-4">Header Styles</h3>

				<div className="flex border-b mb-4">
					<button
						className={`py-2 px-4 ${
							activeStyleTab === "ui"
								? "border-b-2 border-blue-500 text-blue-500"
								: "text-gray-700"
						}`}
						onClick={() => setActiveStyleTab("ui")}>
						UI
					</button>
					<button
						className={`py-2 px-4 ${
							activeStyleTab === "code"
								? "border-b-2 border-blue-500 text-blue-500"
								: "text-gray-700"
						}`}
						onClick={() => setActiveStyleTab("code")}>
						Code
					</button>
				</div>

				{activeStyleTab === "ui" ? (
					<div className="mt-4">
						<div className="mb-4">
							<label className="block text-sm font-medium mb-1">
								Background Color
							</label>
							<div className="flex gap-2">
								<input
									type="color"
									value={state.styles.background}
									onChange={(e) =>
										dispatch({
											type: "UPDATE_HEADER_STYLES",
											payload: {
												background: e.target.value,
											},
										})
									}
									className="w-10 h-10"
								/>
								<input
									type="text"
									className="flex-1 p-2 border rounded"
									value={state.styles.background}
									onChange={(e) =>
										dispatch({
											type: "UPDATE_HEADER_STYLES",
											payload: {
												background: e.target.value,
											},
										})
									}
								/>
							</div>
						</div>

						<div className="mb-4">
							<label className="block text-sm font-medium mb-1">
								Padding
							</label>
							<input
								type="text"
								className="w-full p-2 border rounded"
								value={state.styles.padding}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { padding: e.target.value },
									})
								}
							/>
						</div>

						<div className="mb-4">
							<label className="block text-sm font-medium mb-1">
								Box Shadow
							</label>
							<input
								type="text"
								className="w-full p-2 border rounded"
								value={state.styles.boxShadow}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { boxShadow: e.target.value },
									})
								}
							/>
						</div>

						<div className="mb-4">
							<label className="block text-sm font-medium mb-1">
								Border
							</label>
							<input
								type="text"
								className="w-full p-2 border rounded"
								value={state.styles.border || ""}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_HEADER_STYLES",
										payload: { border: e.target.value },
									})
								}
							/>
						</div>
					</div>
				) : (
					<div className="mt-4">
						<textarea
							className="w-full p-2 border rounded font-mono"
							rows={10}
							value={cssObjectToText(state.styles)}
							onChange={(e) => {
								const styles = parseCssText(e.target.value);
								handleHeaderStyleChange(styles);
							}}
						/>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="p-4">
			<h3 className="text-lg font-medium mb-4">
				{selectedComponent.type.charAt(0).toUpperCase() +
					selectedComponent.type.slice(1)}{" "}
				Styles
			</h3>

			<div className="flex border-b mb-4">
				<button
					className={`py-2 px-4 ${
						activeStyleTab === "ui"
							? "border-b-2 border-blue-500 text-blue-500"
							: "text-gray-700"
					}`}
					onClick={() => setActiveStyleTab("ui")}>
					UI
				</button>
				<button
					className={`py-2 px-4 ${
						activeStyleTab === "code"
							? "border-b-2 border-blue-500 text-blue-500"
							: "text-gray-700"
					}`}
					onClick={() => setActiveStyleTab("code")}>
					Code
				</button>
			</div>

			{activeStyleTab === "ui" ? (
				<div className="mt-4">
					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Padding
						</label>
						<input
							type="text"
							className="w-full p-2 border rounded"
							value={selectedComponent.styles?.padding || ""}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: selectedComponent.id,
										updates: {
											styles: {
												...selectedComponent.styles,
												padding: e.target.value,
											},
										},
									},
								})
							}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Margin
						</label>
						<input
							type="text"
							className="w-full p-2 border rounded"
							value={selectedComponent.styles?.margin || ""}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: selectedComponent.id,
										updates: {
											styles: {
												...selectedComponent.styles,
												margin: e.target.value,
											},
										},
									},
								})
							}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Background Color
						</label>
						<div className="flex gap-2">
							<input
								type="color"
								value={
									selectedComponent.styles?.background ||
									"#ffffff"
								}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_COMPONENT",
										payload: {
											id: selectedComponent.id,
											updates: {
												styles: {
													...selectedComponent.styles,
													background: e.target.value,
												},
											},
										},
									})
								}
								className="w-10 h-10"
							/>
							<input
								type="text"
								className="flex-1 p-2 border rounded"
								value={
									selectedComponent.styles?.background || ""
								}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_COMPONENT",
										payload: {
											id: selectedComponent.id,
											updates: {
												styles: {
													...selectedComponent.styles,
													background: e.target.value,
												},
											},
										},
									})
								}
							/>
						</div>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Text Color
						</label>
						<div className="flex gap-2">
							<input
								type="color"
								value={
									selectedComponent.styles?.color || "#000000"
								}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_COMPONENT",
										payload: {
											id: selectedComponent.id,
											updates: {
												styles: {
													...selectedComponent.styles,
													color: e.target.value,
												},
											},
										},
									})
								}
								className="w-10 h-10"
							/>
							<input
								type="text"
								className="flex-1 p-2 border rounded"
								value={selectedComponent.styles?.color || ""}
								onChange={(e) =>
									dispatch({
										type: "UPDATE_COMPONENT",
										payload: {
											id: selectedComponent.id,
											updates: {
												styles: {
													...selectedComponent.styles,
													color: e.target.value,
												},
											},
										},
									})
								}
							/>
						</div>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Border
						</label>
						<input
							type="text"
							className="w-full p-2 border rounded"
							value={selectedComponent.styles?.border || ""}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: selectedComponent.id,
										updates: {
											styles: {
												...selectedComponent.styles,
												border: e.target.value,
											},
										},
									},
								})
							}
						/>
					</div>

					<div className="mb-4">
						<label className="block text-sm font-medium mb-1">
							Border Radius
						</label>
						<input
							type="text"
							className="w-full p-2 border rounded"
							value={selectedComponent.styles?.borderRadius || ""}
							onChange={(e) =>
								dispatch({
									type: "UPDATE_COMPONENT",
									payload: {
										id: selectedComponent.id,
										updates: {
											styles: {
												...selectedComponent.styles,
												borderRadius: e.target.value,
											},
										},
									},
								})
							}
						/>
					</div>
				</div>
			) : (
				<div className="mt-4">
					<textarea
						className="w-full p-2 border rounded font-mono"
						rows={10}
						value={cssObjectToText(selectedComponent.styles as any)}
						onChange={(e) => {
							const styles = parseCssText(e.target.value);
							handleComponentStyleChange(styles);
						}}
					/>
				</div>
			)}
		</div>
	);
};

export default StylesPanel;
